/**
 * Centered mid-app Sign UI — dApp signature for Phantom / inject / Crossmint path.
 * Does not replace wallet extension popup; prepares + confirms before signMessage.
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BRAND } from "@shared/brand";
import {
  Loader2,
  ShieldCheck,
  Wallet,
  ExternalLink,
  AlertCircle,
} from "lucide-react";
import type { SignDetail } from "@/contexts/dapp-sign-context";

type Phase = "ready" | "signing" | "error";

export function DappSignModal(props: {
  open: boolean;
  title: string;
  description: string;
  details: SignDetail[];
  walletLabel: string;
  walletAddress: string | null;
  canSign: boolean;
  isCustodialOnly: boolean;
  phase: Phase;
  error?: string;
  messagePreview?: string;
  onConfirm: () => void;
  onCancel: () => void;
  onConnect: () => void;
  onOpenGrudgeWallet: () => void;
}) {
  const {
    open,
    title,
    description,
    details,
    walletLabel,
    walletAddress,
    canSign,
    isCustodialOnly,
    phase,
    error,
    messagePreview,
    onConfirm,
    onCancel,
    onConnect,
    onOpenGrudgeWallet,
  } = props;

  const busy = phase === "signing";
  const hint = walletAddress
    ? `${walletAddress.slice(0, 4)}…${walletAddress.slice(-4)}`
    : "—";

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !busy) onCancel();
      }}
    >
      <DialogContent
        className="dapp-sign-modal sm:max-w-md border-amber-500/30 bg-[#0c1220]/95 text-white shadow-2xl backdrop-blur-md"
        style={{
          boxShadow: `0 0 0 1px ${BRAND.gold}33, 0 24px 80px rgba(0,0,0,.55)`,
        }}
        onPointerDownOutside={(e) => {
          if (busy) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (busy) e.preventDefault();
        }}
      >
        <DialogHeader className="space-y-2 text-left">
          <div className="flex items-center gap-2 text-amber-400">
            <ShieldCheck className="h-5 w-5 shrink-0" />
            <span className="text-xs font-semibold uppercase tracking-wider">
              BUDB · dApp signature
            </span>
          </div>
          <DialogTitle className="text-xl font-bold text-white">
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed text-white/70">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div
            className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/30 px-3 py-2.5"
            data-testid="dapp-sign-wallet-chip"
          >
            <Wallet className="h-5 w-5 text-violet-400" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-white">{walletLabel}</div>
              <div className="truncate font-mono text-xs text-white/50">
                {hint}
              </div>
            </div>
            {canSign ? (
              <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-400">
                Ready
              </span>
            ) : (
              <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-400">
                Connect
              </span>
            )}
          </div>

          {details.length > 0 && (
            <dl className="grid gap-1.5 rounded-xl border border-white/10 bg-black/20 p-3 text-sm">
              {details.map((d) => (
                <div
                  key={d.label}
                  className="flex items-baseline justify-between gap-3"
                >
                  <dt className="text-white/50">{d.label}</dt>
                  <dd className="text-right font-medium text-amber-100/90">
                    {d.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}

          {isCustodialOnly && (
            <div className="flex gap-2 rounded-lg border border-sky-500/25 bg-sky-500/10 px-3 py-2 text-xs text-sky-100">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                This address has no Phantom signer in this browser. Connect
                Phantom (extension or Google / Apple) to sign.
              </p>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
              {error}
            </div>
          )}

          {messagePreview && (
            <details className="rounded-lg border border-white/5 bg-black/20 text-[10px] text-white/40">
              <summary className="cursor-pointer px-2 py-1.5 hover:text-white/60">
                Challenge message (what your wallet will sign)
              </summary>
              <pre className="max-h-28 overflow-auto whitespace-pre-wrap break-all px-2 pb-2 font-mono">
                {messagePreview}
              </pre>
            </details>
          )}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            onClick={onCancel}
            className="text-white/70 hover:bg-white/5 hover:text-white"
          >
            Cancel
          </Button>
          {!canSign && (
            <>
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={onConnect}
                className="border-violet-400/40 bg-violet-500/10 text-violet-100 hover:bg-violet-500/20"
              >
                <Wallet className="mr-1.5 h-4 w-4" />
                Connect wallet
              </Button>
              {isCustodialOnly && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={onOpenGrudgeWallet}
                  className="border-sky-400/40 bg-sky-500/10 text-sky-100"
                >
                  <ExternalLink className="mr-1.5 h-4 w-4" />
                  Grudge Wallet
                </Button>
              )}
            </>
          )}
          <Button
            type="button"
            disabled={busy || !walletAddress}
            onClick={onConfirm}
            className="bg-gradient-to-r from-amber-600 to-amber-500 font-semibold text-black hover:from-amber-500 hover:to-amber-400"
            data-testid="dapp-sign-confirm"
          >
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Waiting for wallet…
              </>
            ) : (
              <>
                <ShieldCheck className="mr-2 h-4 w-4" />
                Sign with {walletLabel}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
